import { Injectable } from '@angular/core';
import { Subject, Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { ProductDetailViewModel } from '../models/product-detail-view-model';
import { ProductAnalyticsService } from '@usf/ngrx-product';

@Injectable({
  providedIn: 'root',
})
export class PowerReviewAnalyticsService {
  // set the time to wait before binding elements,
  // elements should be rendered within this time
  private readonly BINDING_DEBOUNCE_TIME = 2000;

  private readonly ratingsAndReviewsSelectors = {
    SHOW_MORE_BUTTON: '.pr-rd-show-more',
    THUMBS_UP_BUTTON: '.pr-helpful-yes',
    THUMBS_DOWN_BUTTON: '.pr-helpful-no',
    SEARCH_BUTTON: '.pr-rd-search-reviews-icon-button',
    SEARCH_INPUT: 'div.pr-rd-search-reviews-input input',
    RATING_LIST_ITEM: '.pr-ratings-histogram-list-item',
    FILTER_BUTTON: '.pr-review-filter-info-bar .pr-filter-btn',
  };

  private readonly qaSelectors = {
    SHOW_MORE_BUTTON: '.pr-qa-display-show-more-questions',
    THUMBS_UP_BUTTON: '.pr-helpful-yes',
    THUMBS_DOWN_BUTTON: '.pr-helpful-no',
    SEARCH_INPUT: '.pr-qa-display-searchbar-input',
    SEARCH_BUTTON: '.pr-qa-search-btn',
    SORT_DROPDOWN: '.pr-qa-sort',
  };

  private readonly ratingsAndReviewsContainer =
    '.power-reviews-detail-container';

  private readonly qaContainer = '.pwr-product-details-questions-container';

  product: ProductDetailViewModel;
  reviewsBindingSubject = new Subject<void>();
  qaBindingSubject = new Subject<void>();

  reviewsBindingSubject$: Subscription;
  qaBindingSubject$: Subscription;

  private ratingsAndReviewsClickListener = (() => {
    this.trackRatingsAndReviewsUserEngagementEvent();
  }).bind(this);

  private ratingsAndReviewsEnterKeydownListener = ((event: KeyboardEvent) => {
    if (event?.code === 'Enter') {
      this.trackRatingsAndReviewsUserEngagementEvent();
    }
  }).bind(this);

  private qaClickListener = (() => {
    this.trackQAUserEngagementEvent();
  }).bind(this);

  private qaEnterKeydownListener = ((event: KeyboardEvent) => {
    if (event?.code === 'Enter') {
      this.trackQAUserEngagementEvent();
    }
  }).bind(this);

  private qaDropdownChangeListener = (() => {
    this.trackQAUserEngagementEvent();
  }).bind(this);

  constructor(private analyticsService: ProductAnalyticsService) {
    this.reviewsBindingSubject$ = this.reviewsBindingSubject
      .pipe(debounceTime(this.BINDING_DEBOUNCE_TIME))
      .subscribe(() => {
        this.setReviewsBindings();
      });

    this.qaBindingSubject$ = this.qaBindingSubject
      .pipe(debounceTime(this.BINDING_DEBOUNCE_TIME))
      .subscribe(() => {
        this.setQABindings();
      });
  }

  bindReviewsEvents(product: ProductDetailViewModel) {
    this.reviewsBindingSubject.next();
    this.product = product;
  }

  bindQAEvents(product: ProductDetailViewModel) {
    this.qaBindingSubject.next();
    this.product = product;
  }

  private rebindReviewsEvents() {
    this.reviewsBindingSubject.next();
  }

  private rebindQAEvents() {
    this.qaBindingSubject.next();
  }

  // Ratings and Reviews impl
  private setReviewsBindings() {
    this.bindRatingsAndReviewsEvent(
      this.ratingsAndReviewsSelectors.SHOW_MORE_BUTTON,
      this.ratingsAndReviewsClickListener,
    );
    this.bindRatingsAndReviewsEvent(
      this.ratingsAndReviewsSelectors.THUMBS_UP_BUTTON,
      this.ratingsAndReviewsClickListener,
    );
    this.bindRatingsAndReviewsEvent(
      this.ratingsAndReviewsSelectors.THUMBS_DOWN_BUTTON,
      this.ratingsAndReviewsClickListener,
    );
    this.bindRatingsAndReviewsEvent(
      this.ratingsAndReviewsSelectors.SEARCH_BUTTON,
      this.ratingsAndReviewsClickListener,
    );
    this.bindRatingsAndReviewsEvent(
      this.ratingsAndReviewsSelectors.RATING_LIST_ITEM,
      this.ratingsAndReviewsClickListener,
    );
    this.bindRatingsAndReviewsEvent(
      this.ratingsAndReviewsSelectors.FILTER_BUTTON,
      this.ratingsAndReviewsClickListener,
    );
    this.bindRatingsAndReviewsEvent(
      this.ratingsAndReviewsSelectors.SEARCH_INPUT,
      this.ratingsAndReviewsEnterKeydownListener,
      'keydown',
    );
  }

  private bindRatingsAndReviewsEvent(
    elementSelector: string,
    listener: any,
    eventType: string = 'click',
  ) {
    const elements = this.selectRnRElements(elementSelector);
    if (elements) {
      elements.forEach(element => {
        element.removeEventListener(eventType, listener);
        element.addEventListener(eventType, listener);
      });
    }
  }

  private selectRnRElements(elementSelector: string): Element[] {
    return Array.from(
      document.querySelectorAll(
        `${this.ratingsAndReviewsContainer} ${elementSelector}`,
      ),
    );
  }

  private trackRatingsAndReviewsUserEngagementEvent() {
    this.analyticsService.trackRatingsAndReviewsUserEngagement(
      this.product?.summary?.divisionNumber,
      this.product?.productNumber,
      this.product?.trackingAttributes,
    );
    this.rebindReviewsEvents();
  }

  // Q&A Impl
  private setQABindings() {
    this.bindQAEvent(this.qaSelectors.SEARCH_BUTTON, this.qaClickListener);

    this.bindQAEvent(
      this.qaSelectors.SEARCH_INPUT,
      this.qaEnterKeydownListener,
    );

    this.bindQAEvent(this.qaSelectors.SHOW_MORE_BUTTON, this.qaClickListener);

    this.bindQAEvent(this.qaSelectors.THUMBS_DOWN_BUTTON, this.qaClickListener);

    this.bindQAEvent(this.qaSelectors.THUMBS_UP_BUTTON, this.qaClickListener);

    this.bindQAEvent(
      this.qaSelectors.SORT_DROPDOWN,
      this.qaDropdownChangeListener,
      'change',
    );
  }

  private bindQAEvent(
    elementSelector: string,
    listener: any,
    eventType: string = 'click',
  ) {
    const elements = this.selectQAElements(elementSelector);
    if (elements) {
      elements.forEach(element => {
        element.removeEventListener(eventType, listener);
        element.addEventListener(eventType, listener);
      });
    }
  }

  private selectQAElements(elementSelector: string): Element[] {
    return Array.from(
      document.querySelectorAll(`${this.qaContainer} ${elementSelector}`),
    );
  }

  private trackQAUserEngagementEvent() {
    this.analyticsService.trackQAEngagement(
      this.product?.summary?.divisionNumber,
      this.product?.productNumber,
      this.product?.trackingAttributes,
    );
    this.rebindQAEvents();
  }
}
